import React, { FC, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { GuestShell } from '../components/guest-shell';
import { NameForm } from '../components/name-form';
import {
  apiErrorMessage,
  applyToken,
  createTable,
  leaveTable,
  startGuestSession,
  useSessionInfo,
} from '../session';
import { clampSeats, MAX_SEATS, MIN_SEATS } from '../lib/names';
import { GUEST_ROUTES } from '../routes.definitions';

const SeatPicker: FC<{ value: number; onChange: (n: number) => void }> = ({
  value,
  onChange,
}) => (
  <div className="st-stack" role="group" aria-labelledby="seat-label">
    <span id="seat-label" className="st-label">
      How many players?
    </span>
    <div className="st-stepper">
      <button
        type="button"
        className="st-btn st-btn--on-ivory"
        aria-label="One fewer player"
        disabled={value <= MIN_SEATS}
        onClick={() => onChange(clampSeats(value - 1))}
      >
        −
      </button>
      <output className="st-stepper__value" aria-live="polite">
        {value}
        <small>players, including you</small>
      </output>
      <button
        type="button"
        className="st-btn st-btn--on-ivory"
        aria-label="One more player"
        disabled={value >= MAX_SEATS}
        onClick={() => onChange(clampSeats(value + 1))}
      >
        +
      </button>
    </div>
    <div className="st-pips" aria-hidden="true">
      {Array.from({ length: value }).map((_, i) => (
        <span key={i} className="st-pip" />
      ))}
    </div>
    <p className="st-hint">
      {MIN_SEATS}–{MAX_SEATS} players. The game starts automatically when every
      seat is filled.
    </p>
  </div>
);

export const NewTablePage: FC = () => {
  const nav = useNavigate();
  const { isAccount, isGuest, name, token, isInRoom } = useSessionInfo();
  const [seats, setSeats] = useState(4);
  const [editingName, setEditingName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Accounts keep their existing create-room flow (coins, friend invites).
  if (isAccount) {
    return <Navigate to="/create-room" replace />;
  }

  const onName = async (newName: string) => {
    setError(null);
    try {
      await startGuestSession(newName);
      setEditingName(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your name. Try again.'));
    }
  };

  const onCreate = async () => {
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      const roomToken = await createTable(token, seats);
      await applyToken(roomToken);
      nav(GUEST_ROUTES.TABLE, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not open a table. Try again.'));
      setBusy(false);
    }
  };

  const onLeaveOld = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await applyToken(await leaveTable(token));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not leave your current table.'));
    } finally {
      setBusy(false);
    }
  };

  const needsName = !isGuest || editingName;

  return (
    <GuestShell
      title="Start a table"
      right={
        <Link to="/" className="st-btn st-btn--ghost st-btn--small">
          Back
        </Link>
      }
    >
      <div className="st-stack" style={{ ['--st-gap' as string]: '20px' }}>
        <ol
          className="st-row st-small st-muted"
          aria-label="Steps"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          <li aria-current={needsName ? 'step' : undefined}>
            <strong
              style={{ color: needsName ? 'var(--st-saffron-2)' : undefined }}
            >
              1 · Your name
            </strong>
          </li>
          <li aria-hidden="true">→</li>
          <li aria-current={!needsName ? 'step' : undefined}>
            <strong
              style={{ color: !needsName ? 'var(--st-saffron-2)' : undefined }}
            >
              2 · Seats
            </strong>
          </li>
          <li aria-hidden="true">→</li>
          <li>3 · Share link</li>
        </ol>

        {needsName ? (
          <>
            <h1 className="st-h2">What should the table call you?</h1>
            <div className="st-card" data-suit="diamonds">
              <NameForm
                defaultName={name}
                submitLabel="Continue"
                busyLabel="Saving your seat…"
                onSubmit={onName}
                serverError={error}
              />
            </div>
          </>
        ) : isInRoom ? (
          <>
            <h1 className="st-h2">You&apos;re already seated</h1>
            <div className="st-card st-stack" data-suit="spades">
              <p className="st-muted">
                You can only sit at one table at a time. Head back, or leave
                that table to start a fresh one.
              </p>
              <Link
                to={GUEST_ROUTES.TABLE}
                className="st-btn st-btn--primary st-btn--block"
              >
                Return to my table
              </Link>
              <button
                type="button"
                className="st-btn st-btn--on-ivory st-btn--block"
                onClick={onLeaveOld}
                disabled={busy}
              >
                {busy ? 'Leaving…' : 'Leave it and start fresh'}
              </button>
              {error && (
                <p className="st-error" role="alert">
                  {error}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <h1 className="st-h2">
              Pull up chairs,{' '}
              <em style={{ color: 'var(--st-saffron)' }}>{name}</em>
            </h1>
            <div className="st-card st-stack" data-suit="spades">
              <SeatPicker value={seats} onChange={setSeats} />
              <button
                type="button"
                className="st-btn st-btn--primary st-btn--block"
                onClick={onCreate}
                disabled={busy}
                aria-busy={busy}
              >
                {busy ? 'Opening your table…' : 'Create table & get link'}
              </button>
              {error && (
                <p className="st-error" role="alert">
                  {error}
                </p>
              )}
            </div>
            <p className="st-small st-muted">
              Not {name}?{' '}
              <button
                type="button"
                className="st-link"
                onClick={() => setEditingName(true)}
              >
                Change name
              </button>
            </p>
          </>
        )}
      </div>
    </GuestShell>
  );
};
